/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { IndianRupee } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type InvoiceStatus, type OutstandingInvoice } from '../../lib/api/finance.js';
import { useFinanceOverview, useRemindInvoice } from '../../lib/queries/finance.js';
import { formatInr } from '../../lib/utils/money.js';

const STATUS_TONE: Readonly<Record<InvoiceStatus, BadgeTone>> = {
  draft: 'slate',
  sent: 'violet',
  partial: 'amber',
  paid: 'green',
  overdue: 'rose',
  void: 'slate',
};

const CARD_GRADIENTS = {
  revenue: 'from-emerald-500 to-emerald-600',
  outstanding: 'from-amber-500 to-orange-500',
  processed: 'from-blue-500 to-blue-600',
  profit: 'from-violet-500 to-purple-600',
} as const;

function GradientCard({
  label,
  value,
  gradient,
}: {
  readonly label: string;
  readonly value: string;
  readonly gradient: string;
}): JSX.Element {
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${gradient} p-5 text-white`}>
      <p className="text-sm text-white/80">{label}</p>
      <p className="mt-2 text-3xl font-bold">{value}</p>
    </div>
  );
}

function OutstandingRow({ invoice }: { readonly invoice: OutstandingInvoice }): JSX.Element {
  const { t } = useTranslation();
  const remind = useRemindInvoice();
  const [sent, setSent] = useState(false);
  const onRemind = useCallback(() => {
    remind.mutate(invoice.id, {
      onSuccess: () => {
        setSent(true);
      },
    });
  }, [remind, invoice.id]);
  return (
    <li className="flex items-center gap-3 py-3 first:pt-0">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600 dark:bg-amber-500/10">
        <IndianRupee className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
          {invoice.clientName}
        </p>
        <p className="truncate text-xs text-slate-400">
          {invoice.invoiceNumber}
          {invoice.dueDate !== null
            ? ` • ${t('admin.financeOverview.due', { date: invoice.dueDate })}`
            : ''}
        </p>
      </div>
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        {formatInr(invoice.balancePaise)}
      </span>
      <Badge label={t(`invoiceStatus.${invoice.status}`)} tone={STATUS_TONE[invoice.status]} />
      <Button variant="outline" size="sm" onClick={onRemind} disabled={remind.isPending || sent}>
        {sent ? t('admin.financeOverview.reminderSent') : t('admin.financeOverview.sendReminder')}
      </Button>
    </li>
  );
}

/** Admin → Finance Overview (Figma): 4 gradient metric cards + outstanding invoices. */
export function FinanceOverview(): JSX.Element {
  const { t } = useTranslation();
  const overview = useFinanceOverview();
  const monthLabel = new Date().toLocaleString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          {t('admin.financeOverview.title')}
        </h1>
        <p className="text-sm text-slate-400">
          {t('admin.financeOverview.subtitle', { month: monthLabel })}
        </p>
      </div>

      <QueryState
        isLoading={overview.isLoading}
        isError={overview.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel=""
      >
        {overview.data !== undefined && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <GradientCard
                label={t('admin.financeOverview.totalRevenue')}
                value={formatInr(overview.data.revenuePaise)}
                gradient={CARD_GRADIENTS.revenue}
              />
              <GradientCard
                label={t('admin.financeOverview.outstanding')}
                value={formatInr(overview.data.outstandingPaise)}
                gradient={CARD_GRADIENTS.outstanding}
              />
              <GradientCard
                label={t('admin.financeOverview.processedToday')}
                value={formatInr(overview.data.processedTodayPaise)}
                gradient={CARD_GRADIENTS.processed}
              />
              <GradientCard
                label={t('admin.financeOverview.netProfit')}
                value={formatInr(overview.data.netProfitPaise)}
                gradient={CARD_GRADIENTS.profit}
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
              <p className="mb-2 font-semibold text-slate-800 dark:text-slate-100">
                {t('admin.financeOverview.outstandingInvoices')}
              </p>
              {overview.data.outstanding.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">
                  {t('admin.financeOverview.noOutstanding')}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {overview.data.outstanding.map((invoice) => (
                    <OutstandingRow key={invoice.id} invoice={invoice} />
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}
